/**
 * SnapCarousel
 *
 * Generic horizontal snap-scroll carousel: a header (title + optional action +
 * arrow controls) over a native `overflow-x` track with CSS scroll-snap. No
 * carousel library — extracted from `BaySelector`/`BayCarousel`, which keep
 * rendering byte-identically on top of it. `SnapCarouselTrack` is the track
 * alone, for a caller that supplies its own header (see `BayCarousel`).
 */

import { useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import type { Key, ReactNode, Ref } from 'react';
import { ChevronIcon } from '#icons';

export interface SnapCarouselHandle {
  scroll: (direction: 'left' | 'right') => void;
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

export interface SnapCarouselTrackProps<T> {
  items: readonly T[];
  getKey: (item: T) => Key;
  renderItem: (item: T, index: number) => ReactNode;
  /** Replaces the default track classes entirely (no merge). Default = BayCarousel's. */
  trackClassName?: string;
  /** Replaces the default item-wrapper classes entirely. Default = BayCarousel's. */
  itemClassName?: string;
  /** `list` renders <ul role="list"> / <li>; default renders <div>/<div> (BayCarousel's DOM). */
  as?: 'div' | 'list';
  id?: string;
  'aria-label'?: string;
  'data-testid'?: string;
  onScrollStateChange?: (canScrollLeft: boolean, canScrollRight: boolean) => void;
  ref?: Ref<SnapCarouselHandle>;
}

const DEFAULT_TRACK_CLASSNAME =
  'flex overflow-x-auto scroll-smooth pb-4 pt-1.5 scrollbar-hide snap-x snap-mandatory';
const DEFAULT_ITEM_CLASSNAME = 'min-w-fit snap-start flex-shrink-0 pl-4 last:pr-4';

// Firefox / IE-Edge scrollbar hiding — matches BayCarousel's inline style verbatim.
const HIDE_SCROLLBAR_STYLE = { scrollbarWidth: 'none', msOverflowStyle: 'none' } as const;

export function SnapCarouselTrack<T>({
  items,
  getKey,
  renderItem,
  trackClassName,
  itemClassName,
  as = 'div',
  id,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  onScrollStateChange,
  ref,
}: SnapCarouselTrackProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = () => {
    if (!scrollRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = scrollRef.current.clientWidth * 0.8;
    const newScrollLeft =
      direction === 'left'
        ? scrollRef.current.scrollLeft - scrollAmount
        : scrollRef.current.scrollLeft + scrollAmount;

    scrollRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  };

  useImperativeHandle(ref, () => ({ scroll, canScrollLeft, canScrollRight }), [canScrollLeft, canScrollRight]);

  useEffect(() => {
    updateScrollButtons();
    const handleResize = () => updateScrollButtons();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [items]);

  useEffect(() => {
    onScrollStateChange?.(canScrollLeft, canScrollRight);
  }, [canScrollLeft, canScrollRight, onScrollStateChange]);

  const resolvedTrackClassName = trackClassName ?? DEFAULT_TRACK_CLASSNAME;
  const resolvedItemClassName = itemClassName ?? DEFAULT_ITEM_CLASSNAME;

  if (as === 'list') {
    return (
      <ul
        ref={scrollRef as unknown as Ref<HTMLUListElement>}
        id={id}
        onScroll={updateScrollButtons}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        role="list"
        className={resolvedTrackClassName}
        style={HIDE_SCROLLBAR_STYLE}
      >
        {items.map((item, index) => (
          <li key={getKey(item)} className={resolvedItemClassName}>
            {renderItem(item, index)}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div
      ref={scrollRef}
      id={id}
      onScroll={updateScrollButtons}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      className={resolvedTrackClassName}
      style={HIDE_SCROLLBAR_STYLE}
    >
      {items.map((item, index) => (
        <div key={getKey(item)} className={resolvedItemClassName}>
          {renderItem(item, index)}
        </div>
      ))}
    </div>
  );
}

export interface SnapCarouselProps<T> extends Omit<SnapCarouselTrackProps<T>, 'ref' | 'onScrollStateChange' | 'id'> {
  title: ReactNode;
  /** Replaces the default heading classes. Default = BaySelector's h2. */
  titleClassName?: string;
  titleTestId?: string;
  /** Replaces the default header-row classes. Default = BaySelector's. */
  headerClassName?: string;
  /** Extra control rendered before the arrows (BaySelector's "Book by Time" pill). */
  headerAction?: ReactNode;
  /** @default true */
  showNavigation?: boolean;
  /** Replaces the arrow-group classes. Default 'flex gap-2'. */
  navigationClassName?: string;
  prevLabel: string;
  nextLabel: string;
  prevTestId?: string;
  nextTestId?: string;
  /** Replaces the default root class entirely (no merge). Default = 'mt-3' (BaySelector's). */
  className?: string;
}

const ARROW_BUTTON_CLASSNAME =
  'rounded-full border border-theme-600 bg-white p-2 shadow-sm transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2';

export function SnapCarousel<T>({
  title,
  titleClassName,
  titleTestId,
  headerClassName,
  headerAction,
  showNavigation = true,
  navigationClassName,
  prevLabel,
  nextLabel,
  prevTestId,
  nextTestId,
  className,
  items,
  getKey,
  renderItem,
  trackClassName,
  itemClassName,
  as,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: SnapCarouselProps<T>) {
  const trackRef = useRef<SnapCarouselHandle>(null);
  const trackId = useId();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const handleScrollStateChange = useCallback((left: boolean, right: boolean) => {
    setCanScrollLeft(left);
    setCanScrollRight(right);
  }, []);

  return (
    <div className={className ?? 'mt-3'}>
      <div className={headerClassName ?? 'mb-6 flex items-center justify-between px-4'}>
        <h2
          className={titleClassName ?? 'text-xl font-semibold leading-none tracking-wide text-butter-gray-900'}
          data-testid={titleTestId}
        >
          {title}
        </h2>

        {headerAction}

        {showNavigation && (
          <div className={navigationClassName ?? 'flex gap-2'}>
            <button
              type="button"
              onClick={() => trackRef.current?.scroll('left')}
              disabled={!canScrollLeft}
              aria-label={prevLabel}
              aria-controls={trackId}
              data-testid={prevTestId}
              className={ARROW_BUTTON_CLASSNAME}
            >
              <ChevronIcon className="size-3 text-xs rotate-180 text-theme-600" ariaHidden />
            </button>

            <button
              type="button"
              onClick={() => trackRef.current?.scroll('right')}
              disabled={!canScrollRight}
              aria-label={nextLabel}
              aria-controls={trackId}
              data-testid={nextTestId}
              className={ARROW_BUTTON_CLASSNAME}
            >
              <ChevronIcon className="size-3 text-xs text-theme-600" ariaHidden />
            </button>
          </div>
        )}
      </div>

      <SnapCarouselTrack
        ref={trackRef}
        id={trackId}
        items={items}
        getKey={getKey}
        renderItem={renderItem}
        trackClassName={trackClassName}
        itemClassName={itemClassName}
        as={as}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        onScrollStateChange={handleScrollStateChange}
      />
    </div>
  );
}
